import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from 'react';
import { Button, Form, Modal, Message } from 'semantic-ui-react';
import DropDown from '../shared/dropdown';
import { reasonTypesIds, reasonTypes, reasonTypesStatuses, statusesReasonTypes } from '../shared/constants'
import { Reservations } from '../../api/services/next';
import { UpdateReservationStatus } from '../../api/services/pms';
import { useTextResources } from '../../language';
import { onError, onSuccess } from '../shared/messages';
import { useLoader } from "../../hooks/useLoader";
import { useConfirm } from "../../hooks/useConfirm";
import { Reasons } from '../../api/services/next';
import RadioSelectionInput from '../shared/RadioSelectionInput';
import { UserHasPermission, PermissionsActions } from '../shared/permissionsActions';

import './style.less';

const CancelReservationPopup = ({ onClose, controller, ...props }) => {
    const text = useTextResources();
    const loader = useLoader();
    const confirm = useConfirm();

    const [open, setOpen] = useState(props.open);
    const [reservation, setReservation] = useState();
    const [reservations, setReservations] = useState([]);
    const [selectedAction, setSelectedAction] = useState();
    const [cancellationReasons, setCancellationReasons] = useState();
    const [isDeleted, setIsDeleted] = useState();
    const [selectedCancellationReason, setSelectedCancellationReason] = useState();
    const [comments, setComments] = useState();
    const isInitialLoad = useRef(false);

    useEffect(() => {
        if (controller) {
            window[controller] = async (action, params) => {
                switch (action) {
                    case "open":
                        initData(params.reservationId, params.reservation);
                        setOpen(true)
                        break;
                    default:
                        break;
                }
            }
        }
    }, []);

    useEffect(() => {
        (async () => {
            if (selectedAction) {
                try {
                    const reasons = await Reasons.GetReasons({ ReasonTypeId: reasonTypesIds[selectedAction] });

                    setCancellationReasons(reasons);

                    if (!isInitialLoad.current) {
                        setSelectedCancellationReason(null);
                        setComments('');
                    }
                } catch (error) {
                    onError(error);
                }
            }
        }
        )();
    }, [selectedAction]);

    useEffect(() => {
        if (cancellationReasons) {
            if (isInitialLoad.current) {
                isInitialLoad.current = false;
                return;
            }

            const reason = cancellationReasons?.find(r => r.Id === selectedCancellationReason);
            setComments(reason?.Description || '');
        }

    }, [selectedCancellationReason, cancellationReasons]);

    useEffect(() => {
        setOpen(props.open)
    }, [props.open]);

    useEffect(() => {
        initData(props.reservationId, props.reservation, props.reservations, props.skipLoadState);
    }, [props.reservation, props.reservationId, props.reservations, props.skipLoadState]);

    const initData = async (reservationId, reservation, multipleReservations, skipLoadState) => {
        loader(true)
        isInitialLoad.current = true;
        try {
            if (multipleReservations && multipleReservations.length > 0) {
                setReservations(multipleReservations);
                setReservation(multipleReservations[0]);
                setSelectedAction(reasonTypes.Cancellation);
                setIsDeleted(false);
                return;
            }

            if (skipLoadState && reservation) {
                setReservation(reservation);
                setReservations([]);
                setSelectedAction(reasonTypes.Cancellation);
                setIsDeleted(false);
                return;
            }

            const [_reservation] = await Promise.all([
                getReservation(reservation, reservationId),
            ])

            if (!_reservation) {
                return;
            }

            setReservations([]);

            if (_reservation.StatusId === reasonTypesStatuses[reasonTypes.Cancellation] ||
                _reservation.StatusId === reasonTypesStatuses[reasonTypes.Deletion]
            ) {
                setIsDeleted(_reservation.StatusId === reasonTypesStatuses[reasonTypes.Deletion]);
                setSelectedAction(statusesReasonTypes[_reservation.StatusId]);
                setSelectedCancellationReason(_reservation.CancellationReasonID);
                setComments(_reservation.CancelReasonDescripiton);
            } else {
                setSelectedAction(reasonTypes.Cancellation);
            }
        } catch (err) {
            onError(err);
        } finally {
            loader(false)
        }
    };

    const getReservation = async (reservation, reservationId) => {
        if (!reservation && reservationId) {
            reservation = await Reservations.GetReservation(reservationId)
        }

        if (!reservation) {
            return;
        }
        setReservation(reservation)
        return reservation;
    };

    const handleClosePopup = (updatedReservation) => {
        if (window["CancelReservationPopupCallback"]) {
            window["CancelReservationPopupCallback"](updatedReservation || null);
        }
        onClose?.(updatedReservation)
        setComments(null);
        setCancellationReasons(null);
        setSelectedAction(null);
        setSelectedCancellationReason(null);
        setOpen(false)
    };
    const validateInputs = () => {
        if (!selectedAction) {
            onError(text("SelectCancellationAction"));
            return false;
        }
        if (!selectedCancellationReason) {
            onError(text("SelectCancellationReason"));
            return false;
        }

        if (selectedAction === reasonTypes.Deletion && !comments) {
            onError(text("ProvideCommentForDeletion"));
            return false;
        }

        return true;
    }
    const save = async () => {
        if (!validateInputs()) {
            return
        }

        const isMultiple = reservations.length > 1;

        if (selectedAction === reasonTypes.Deletion && !isDeleted) {
            const confirmed = await confirm({
                header: text('DeleteReservation'),
                content: text('AreYouSureYouWantToDeleteThisReservation'),
                confirmButton: {
                    content: text('Yes'),
                    positive: true,
                },
                cancelButton: {
                    content: text('No'),
                    negative: true,
                },
            });

            if (!confirmed) {
                return;
            }
        }

        loader(true)
        try {
            const selectedReason = cancellationReasons?.find(r => r.Id === selectedCancellationReason);

            const reservationsToProcess = isMultiple ? reservations : [reservation];
            const updatedReservations = [];

            for (const res of reservationsToProcess) {
                const updatedReservation = {
                    ...res,
                    StatusId: reasonTypesStatuses[selectedAction],
                    CancellationReasonID: selectedCancellationReason,
                    CancelReasonDescripiton: comments,
                    CancellationReasonName: selectedReason?.Name || '',
                    IsDoNotChangeRoom: false,
                };

                await UpdateReservationStatus(updatedReservation);
                updatedReservations.push(updatedReservation);
            }

            const result = isMultiple ? updatedReservations : updatedReservations[0];

            onSuccess();
            handleClosePopup(result)
        } catch (error) {
            onError(error);
        } finally {
            loader(false)
        }
    };


    return (
        <Modal size="small" open={open} onClose={() => handleClosePopup()} className='reservation-policy-popup' closeOnEscape closeOnDimmerClick={false}>
            <Modal.Header>{text('CancelReservation')}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Group>
                        <Form.Field disabled={isDeleted}>
                            <label>{text('ChooseAction')}</label>
                            <RadioSelectionInput
                                name="selectedAction"
                                value={selectedAction}
                                onChange={setSelectedAction}
                                options={[
                                    {
                                        value: reasonTypes.Cancellation,
                                        label: text('CancelReservation'),
                                        description: text('The reservation will remain in the system as cancelled and available for viewing'),
                                        icon: 'x',
                                    },
                                    ...((isDeleted || UserHasPermission(PermissionsActions.DeleteReservation)) ? [{
                                        value: reasonTypes.Deletion,
                                        label: text('DeleteReservation'),
                                        description: text('The reservation will be permanently deleted from the system and cannot be recovered'),
                                        icon: 'trash',
                                    }] : []),
                                ]}
                            />
                        </Form.Field>
                    </Form.Group>
                    <hr style={{ margin: '20px 0' }} />
                    <Form.Group widths={2}>
                        <Form.Field
                            disabled={selectedAction == null}
                            key='Reason'
                            textField='Name'
                            valueField='Id'
                            control={DropDown}
                            placeholder={text('ChooseReason')}
                            data={cancellationReasons}
                            value={selectedCancellationReason}
                            label={text('Reason')}
                            onChange={setSelectedCancellationReason}
                            clearable
                            fluid
                            search
                        />
                    </Form.Group>
                    <hr style={{ margin: '20px 0' }} />
                    <Form.TextArea
                        label={text('Comments')}
                        type="text"
                        placeholder={text('AddAdditionalComments')}
                        value={comments}
                        onChange={(e, { value }) => setComments(value)}
                        rows={3}
                        style={{
                            minWidth: '-webkit-fill-available',
                            maxWidth: 'fit-content',
                            fontSize: 'initial',
                        }}
                    />

                    <div style={{ visibility: selectedAction === reasonTypes.Deletion ? 'visible' : 'hidden' }}>
                        <hr style={{ margin: '20px 0' }} />

                        <Form.Field>
                            <Message warning size="small" visible>
                                <div>
                                    {text('ThisActionWillPermanentlyDeleteTheReservation')}
                                </div>
                                <div>
                                    {text('DeletedReservationsWillNotAppearInReports')}
                                </div>
                                <div>
                                    {text('ThisActionCannotBeUndone')}
                                </div>
                            </Message>
                        </Form.Field>
                    </div>

                </Form>
            </Modal.Content>
            <Modal.Actions style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button positive onClick={save}>
                    {text("Save")}
                </Button>
                <Button negative onClick={() => handleClosePopup()}>
                    {text("Close")}
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

CancelReservationPopup.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    reservation: PropTypes.object,
    reservations: PropTypes.array,
    reservationId: PropTypes.number,
    skipLoadState: PropTypes.bool,
    controller: PropTypes.any,
}

export default CancelReservationPopup
